import axios, { type AxiosInstance, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000/api';

// ── Axios instance ────────────────────────────────────────────
const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// ── Token management ──────────────────────────────────────────
export const tokenStore = {
  get:    ()         => localStorage.getItem('sb_token'),
  set:    (t: string) => localStorage.setItem('sb_token', t),
  remove: ()          => localStorage.removeItem('sb_token'),
};

// ── Request interceptor — attach JWT ──────────────────────────
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStore.get();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Response interceptor — handle 401 ────────────────────────
api.interceptors.response.use(
  (res: AxiosResponse) => res,
  (error) => {
    if (error.response?.status === 401) {
      tokenStore.remove();
      window.dispatchEvent(new CustomEvent('auth:logout'));
    }
    return Promise.reject(error instanceof Error ? error : new Error(error.response?.data?.error || 'Request failed'));
  }
);

export default api;

// ── Typed API helpers ─────────────────────────────────────────
export interface ApiResponse<T> { success: boolean; data?: T; message?: string; error?: string }
export interface Paginated<T> extends ApiResponse<T[]> {
  pagination: { total: number; page: number; limit: number; pages: number }
}

async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const res = await api.get<ApiResponse<T>>(url, { params });
  return res.data.data as T;
}
async function post<T>(url: string, body?: unknown): Promise<T> {
  const res = await api.post<ApiResponse<T>>(url, body);
  return res.data.data as T;
}
async function put<T>(url: string, body?: unknown): Promise<T> {
  const res = await api.put<ApiResponse<T>>(url, body);
  return res.data.data as T;
}
async function patch<T>(url: string, body?: unknown): Promise<T> {
  const res = await api.patch<ApiResponse<T>>(url, body);
  return res.data.data as T;
}
async function del<T>(url: string): Promise<T> {
  const res = await api.delete<ApiResponse<T>>(url);
  return res.data.data as T;
}
async function getPages<T>(url: string, params?: Record<string, unknown>): Promise<Paginated<T>> {
  const res = await api.get<Paginated<T>>(url, { params });
  return res.data;
}

// ════════════════════════════════════════════════════════════
// AUTH
// ════════════════════════════════════════════════════════════
export interface LoginResponse {
  token: string;
  refreshToken: string;
  user: { id: string; name: string; email: string; role: string; title: string; ini: string; color: string };
}
export const authApi = {
  login: (email: string, password: string) => post<LoginResponse>('/auth/login', { email, password }),
  me:    ()                                 => get<LoginResponse['user']>('/auth/me'),
  users: ()                                 => get<LoginResponse['user'][]>('/auth/users'),
};

// ════════════════════════════════════════════════════════════
// COLLATERALS
// ════════════════════════════════════════════════════════════
export interface CollateralData {
  _id: string; id?: string; name: string; type: string; size: string; pages: number;
  tags: string[]; sharedBy: string; sharedByIni: string; sharedDate: string;
  recipients: { name: string; ini: string; org: string; readPct: number; lastSeen: string; views: number }[];
  pdata: { t: string; s: string; b: string; stat?: boolean }[];
  url?: string; demoLogin?: { user: string; pwd: string } | null; demoNote?: string;
}
export const collateralsApi = {
  list:    (params?: Record<string, unknown>)    => getPages<CollateralData>('/collaterals', params),
  get:     (id: string)                          => get<CollateralData>(`/collaterals/${id}`),
  create:  (data: Partial<CollateralData>)       => post<CollateralData>('/collaterals', data),
  update:  (id: string, data: Partial<CollateralData>) => put<CollateralData>(`/collaterals/${id}`, data),
  delete:  (id: string)                          => del<null>(`/collaterals/${id}`),
  trackView: (id: string, body: { recipientName: string; page?: number; durationSec?: number }) =>
                                                    post<null>(`/collaterals/${id}/track`, body),
};

// ════════════════════════════════════════════════════════════
// LEADS
// ════════════════════════════════════════════════════════════
export interface LeadData {
  _id: string; name: string; ini: string; org: string; email: string; phone: string;
  heat: 'hot' | 'warm' | 'cold'; intent: number; lastSeen: string; firstSeen: string;
  sourceType: string; source: string; signals: string[]; sessions: number; totalTime: string;
  status: 'new' | 'contacted' | 'cold'; crmRef?: string;
  aiScore?: number; aiSuggestion?: string;
}
export const leadsApi = {
  list:    (params?: Record<string, unknown>)  => getPages<LeadData>('/leads', params),
  stats:   ()                                  => get<{ hot: number; warm: number; cold: number; total: number; avgIntent: number }>('/leads/stats'),
  get:     (id: string)                        => get<LeadData>(`/leads/${id}`),
  create:  (data: Partial<LeadData>)           => post<LeadData>('/leads', data),
  update:  (id: string, data: Partial<LeadData>) => put<LeadData>(`/leads/${id}`, data),
  delete:  (id: string)                        => del<null>(`/leads/${id}`),
  rescore: (id: string)                        => post<{ score: number; heat: string; reasoning: string }>(`/leads/${id}/rescore`),
};

// ════════════════════════════════════════════════════════════
// PLAYBOOKS
// ════════════════════════════════════════════════════════════
export interface PlaybookData {
  _id: string; name: string; sent: string; status: 'sent' | 'draft'; colls: number[];
  recipients: { name: string; ini: string; org: string }[];
  views: number; opens: number; createdBy: string; createdByIni: string; assignedToMe?: boolean;
  dealRoom: { color: string; accent: string; stage: string; stageIco: string; label: string };
  security: { otp: boolean; expiry: string | null; blockFwd: boolean }; allowShare: boolean;
  landingPage: {
    heroStyle: string; heroBg?: string; heroAccent?: string;
    heroHeadline: string; heroSubline: string; showRepCard: boolean;
    repName: string; repRole: string; repAvatar: string; repCalendly?: string; repWhatsapp?: string;
    personalMsg: string;
    ctaSection: { enabled: boolean; items: { id: string; label: string; icon: string; style: string; enabled: boolean }[] };
  };
}
export const playbooksApi = {
  list:    (params?: Record<string, unknown>)  => getPages<PlaybookData>('/playbooks', params),
  get:     (id: string)                        => get<PlaybookData>(`/playbooks/${id}`),
  create:  (data: Partial<PlaybookData>)       => post<PlaybookData>('/playbooks', data),
  update:  (id: string, data: Partial<PlaybookData>) => put<PlaybookData>(`/playbooks/${id}`, data),
  delete:  (id: string)                        => del<null>(`/playbooks/${id}`),
  trackOpen: (id: string)                      => post<{ views: number }>(`/playbooks/${id}/open`),
};

// ════════════════════════════════════════════════════════════
// NUDGES
// ════════════════════════════════════════════════════════════
export interface NudgeData {
  _id: string; name: string; ini: string; org: string; score: number;
  heat: string; trigger: string; detail: string; intent: string[];
  suggestion: string; template: string; lastActive: string; sent: boolean; aiGenerated?: boolean;
}
export const nudgesApi = {
  list:     ()         => get<NudgeData[]>('/nudges'),
  create:   (data: Partial<NudgeData>) => post<NudgeData>('/nudges', data),
  markSent: (id: string) => patch<NudgeData>(`/nudges/${id}/sent`),
  delete:   (id: string) => del<null>(`/nudges/${id}`),
};

// ════════════════════════════════════════════════════════════
// ANALYTICS
// ════════════════════════════════════════════════════════════
export const analyticsApi = {
  dashboard:  (period?: string)    => get<{ kpi: Record<string, number>; bars: { d: string; v: number }[] }>('/analytics/dashboard', { period }),
  engagement: ()                   => get<{ name: string; type: string; pct: number; views: number }[]>('/analytics/engagement'),
  trackEvent: (data: {
    type: string; collateralId?: string; playbookId?: string; leadId?: string;
    recipientName?: string; page?: number; durationSec?: number; device?: string;
  })                               => post<unknown>('/analytics/events', data),
};

// ════════════════════════════════════════════════════════════
// AI AGENTS
// ════════════════════════════════════════════════════════════
export interface NudgeAgentResult {
  message: string; subject?: string; tone: string; reasoning: string; followUpIn: string; nudgeId?: string;
}
export interface MeetingBriefResult {
  openingLine: string; keyInsights: string[]; suggestedQuestions: string[];
  talkingPoints: string[]; objectionHandling: string[]; nextSteps: string[];
}
export interface PlaybookSuggestionResult {
  recommendedCollaterals: number[]; heroHeadline: string; personalMessage: string; reasoning: string;
}
export interface ContentAnalysisResult {
  summary: string; engagementRating: string; topPerformingPages: number[];
  dropoffPage?: number; suggestions: string[];
}
export interface AiStatus {
  configured: boolean; model: string; agents: string[]; note: string;
}
export interface SalesCoachResult { reply: string; isAI: boolean }

export const aiApi = {
  status:          ()              => get<AiStatus>('/ai/status'),
  generateNudge:   (leadId: string) => post<NudgeAgentResult>(`/ai/nudge/${leadId}`),
  scoreLead:       (leadId: string) => post<{ score: number; heat: string; reasoning: string; topSignals: string[] }>(`/ai/score/${leadId}`),
  meetingBrief:    (leadId: string) => post<MeetingBriefResult>(`/ai/brief/${leadId}`),
  suggestPlaybook: (leadId: string) => post<PlaybookSuggestionResult>(`/ai/suggest/${leadId}`),
  analyzeContent:  (collId: string) => post<ContentAnalysisResult>(`/ai/analyze/${collId}`),
  batchScore:      ()               => post<{ scored: number; errors: string[]; total: number }>('/ai/batch-score'),
  coach:           (message: string, context?: Record<string, unknown>) =>
                                       post<SalesCoachResult>('/ai/coach', { message, context }),
};
